import { Box, Button, Grid, IconButton, styled, Typography } from '@mui/material';
import { FieldArrayRenderProps, FormikProps } from 'formik';
import React from 'react';
import DragHandle from '@mui/icons-material/DragHandle';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Add from '@mui/icons-material/Add';
import { TextFormField } from 'lib/form/fields/TextFormField';
import DeleteIcon from '@mui/icons-material/Delete';
import { TagForm } from './TagsForm';
import { TagsImagePiker } from './TagsImagePicker';

interface TagValuesFormProps extends FieldArrayRenderProps {
    form: FormikProps<TagForm>;
}

interface TagValueFormProps {
    remove: (index: number) => void;
    index: number;
    form: FormikProps<TagForm>;
}

const PREFIX = 'TagValue';

const classes = {
    root: `${PREFIX}-root`,
    dragIcon: `${PREFIX}-dragIcon`,
    iconContainer: `${PREFIX}-iconContainer`
};

const StyledBox = styled(Box)(({ theme }) => ({
    zIndex: 999999,
    backgroundColor: theme.palette.background.paper,
    [`& .${classes.dragIcon}`]: {
        cursor: 'pointer',
        touchAction: 'none'
    },
    [`& .${classes.iconContainer}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

const TagValueForm: React.FC<TagValueFormProps> = ({ remove, index, form }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: index });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
        transition
    };

    const removeItem = React.useCallback(() => {
        remove(index);
    }, [index, remove]);

    return (
        <StyledBox ref={setNodeRef} style={style} zIndex={999999}>
            <Grid container spacing={2}>
                <Grid className={classes.iconContainer} size={1}>
                    <DragHandle className={classes.dragIcon} {...attributes} {...listeners} />
                </Grid>
                <Grid size={2}>
                    <TextFormField type="number" name={`values[${index}].id`} min={0} />
                </Grid>
                <Grid size={2}>
                    <TextFormField type="number" name={`values[${index}].sort`} min={0} />
                </Grid>
                <Grid size={4}>
                    <TextFormField name={`values[${index}].title`} />
                </Grid>

                <Grid size={2}>
                    <TagsImagePiker
                        index={index}
                        name={`values[${index}].imageUrl`}
                        setFieldValue={form.setFieldValue}
                        values={form.values?.values}
                    />
                </Grid>
                <Grid className={classes.iconContainer} size={1}>
                    <IconButton onClick={removeItem} size="large">
                        <DeleteIcon color="error" />
                    </IconButton>
                </Grid>
            </Grid>
        </StyledBox>
    );
};

export const TagValuesForm: React.FC<TagValuesFormProps> = ({ form, remove, move, push }) => {
    const items = React.useMemo(
        () => (form.values?.values || []).map((_: TagForm, index: number) => index),
        [form.values?.values]
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                move(active.id as number, over.id as number);
            }
        },
        [move]
    );

    const handleAddItem = React.useCallback(() => {
        push({ id: '', title: '', sort: '' });
    }, [push]);

    if (Array.isArray(form.values?.values)) {
        return (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <Grid container spacing={2}>
                        <Grid size={1} />
                        <Grid size={2}>
                            <Typography align="center">ID</Typography>
                        </Grid>
                        <Grid size={2}>
                            <Typography align="center">Sort</Typography>
                        </Grid>
                        <Grid size={4}>
                            <Typography align="center">Title</Typography>
                        </Grid>
                        <Grid size={2}>
                            <Typography align="center">Icon</Typography>
                        </Grid>
                    </Grid>
                    {form.values.values.map((_: TagForm, index: number) => (
                        <TagValueForm key={`sortable-${index}`} index={index} remove={remove} form={form} />
                    ))}
                    <Button variant="outlined" onClick={handleAddItem}>
                        <Add />
                    </Button>
                </SortableContext>
            </DndContext>
        );
    }
};
